import { computed } from '@vue/composition-api';
import { QuestionType } from '@/api/v2/requests/surveys';
import { DummyFilter } from '@/components/Table/dummy-filter';
import { StatusBackgroundColor } from '@/views/Brand/Surveys/_constants/survey-status';
const CELL_STYLE = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    height: '100%',
};
export const useSurveyUsersAnswersColumns = (ctx, questions = [], surveyInternalNumber, fetchResponses, onPlaceClick) => {
    const baseColumns = computed(() => [
        {
            field: 'user.place.number',
            headerName: ctx.root.$t('brand-surveys.details.place-number').toString(),
            valueGetter: (params) => params.data?.user?.place?.number || '-',
            sortable: true,
            width: 150,
            minWidth: 150,
            filter: DummyFilter,
            floatingFilter: true,
            floatingFilterComponent: 'FloatingTextFilter',
            suppressHeaderFilterButton: true,
            suppressFloatingFilterButton: true,
            cellStyle: CELL_STYLE,
        },
        {
            field: 'user.place.name',
            headerName: ctx.root.$t('brand-surveys.details.place-name').toString(),
            valueGetter: (params) => params.data?.user?.place?.name || '-',
            sortable: true,
            minWidth: 250,
            filter: DummyFilter,
            floatingFilter: true,
            floatingFilterComponent: 'FloatingTextFilter',
            suppressHeaderFilterButton: true,
            suppressFloatingFilterButton: true,
            cellStyle: {
                ...CELL_STYLE,
                ...(onPlaceClick && { cursor: 'pointer' }),
            },
            onCellClicked: onPlaceClick
                ? (params) => {
                    if (params.data) {
                        onPlaceClick(params.data);
                    }
                }
                : undefined,
        },
        {
            field: 'user.fullName',
            headerName: ctx.root.$t('brand-surveys.details.full-name').toString(),
            valueGetter: (params) => params.data?.user?.fullName || '-',
            sortable: true,
            flex: 1,
            minWidth: 250,
            filter: DummyFilter,
            floatingFilter: true,
            floatingFilterComponent: 'FloatingTextFilter',
            suppressHeaderFilterButton: true,
            suppressFloatingFilterButton: true,
            cellStyle: CELL_STYLE,
        },
    ]);
    const getFilterConfigForQuestionType = (question, index) => {
        switch (question.type.toLowerCase()) {
            case QuestionType.TEXT:
                return {
                    filter: DummyFilter,
                    floatingFilter: true,
                    floatingFilterComponent: 'FloatingTextFilter',
                    suppressHeaderFilterButton: true,
                    suppressFloatingFilterButton: true,
                };
            case QuestionType.RADIO:
                return {
                    filter: DummyFilter,
                    floatingFilter: true,
                    floatingFilterComponent: 'FloatingSelectFilter',
                    floatingFilterComponentParams: () => ({
                        options: question.options?.map((option) => ({
                            value: option.label,
                            label: option.label,
                        })) || [],
                        getLabel: (value) => value,
                    }),
                    suppressHeaderFilterButton: true,
                    suppressFloatingFilterButton: true,
                };
            case QuestionType.CHECKBOX:
                return {
                    filter: DummyFilter,
                    floatingFilter: true,
                    floatingFilterComponent: 'FloatingSelectFilter',
                    floatingFilterComponentParams: () => {
                        const allOptions = new Set();
                        question.options?.forEach((option) => {
                            if (option.label) {
                                allOptions.add(option.label);
                            }
                        });
                        return {
                            options: Array.from(allOptions).map((option) => ({
                                value: option,
                                label: option,
                            })),
                            getLabel: (value) => value,
                        };
                    },
                    suppressHeaderFilterButton: true,
                    suppressFloatingFilterButton: true,
                };
            case 'file':
            default:
                return {};
        }
    };
    const colorStatusMap = {
        REJECTED: StatusBackgroundColor.REJECTED,
        APPROVED: StatusBackgroundColor.APPROVED,
        NEEDS_REVISION: StatusBackgroundColor.NEEDS_REVISION,
    };
    const questionColumns = computed(() => {
        return questions.map((question, index) => {
            const filterConfig = getFilterConfigForQuestionType(question, index);
            return {
                field: `answers_${index}`,
                headerName: question.name,
                valueGetter: (params) => {
                    return params.data?.answers?.[index] || '';
                },
                sortable: false,
                minWidth: 260,
                cellRenderer: 'SurveyAnswerCell',
                wrapHeaderText: true,
                autoHeaderHeight: true,
                cellRendererParams: (params) => ({
                    question,
                    answerIndex: index,
                    imageMode: false,
                    surveyInternalNumber,
                    surveyUser: params.data,
                    onDataUpdate: fetchResponses,
                }),
                ...filterConfig,
                cellStyle: (params) => ({
                    ...CELL_STYLE,
                    backgroundColor: colorStatusMap[String(params.data?.statuses?.[index])] || 'transparent',
                    borderLeft: '1px solid #e0e0e0',
                    fontWeight: 400,
                }),
            };
        });
    });
    const columns = computed(() => {
        return [...baseColumns.value, ...questionColumns.value];
    });
    return { columns };
};
